import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/styles";
import { FormControlLabel, Checkbox, Snackbar, Divider } from "@material-ui/core";
import { ReflexContainer, ReflexSplitter, ReflexElement } from "react-reflex";
import { Alert } from "@material-ui/lab";
import { debounce } from "lodash";

import uiConf from "../../config/uiConf";

import apiClient from "../../lib/common/apiClient";
import checkValidation from "../../lib/utils/checkValidation";
import checkConditionWithMsg from "../../lib/utils/checkConditionWithMsg";

import Dialog from "../dialog/functional/Dialog";
import DialogHeader from "../dialog/functional/DialogHeader";
import DialogContent from "../dialog/functional/DialogContent";
import DialogTitle from "../dialog/functional/DialogTitle";
import DialogActionsOkCancel from "../dialog/functional/DialogActionsOkCancel";
import AutoForm from "../form/functional/AutoForm";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import failureKey from "../../lib/common/failureKey";

const useStyles = makeStyles((theme) => ({
    keepOpenSwitch: {
        marginLeft: "8px",
    },

    label: {
        fontSize: "smaller",
        color: theme.palette.primary.text,
    },
}));

function MomSysOrderBuyerDetailDialog(props) {
    const {
        id,
        schema,

        moveable,
        resizable,
        fullWidth,
        maxWidth,

        onClose,
        onInitialized,
        onSuccess,

        title,
        formId,

        onDialogUpdateDetail,
        onSelectMst,

        customizeErrorMsgCallback,

        ...others
    } = props;

    const classes = useStyles();
    const { t } = useTranslation();

    const [dialogInstance, setDialogInstance] = useState();
    const [okButtonDisabled, setOkButtonDisabled] = useState(true);
    const [formData, setFormData] = useState();
    const [keepOpen, setKeepOpen] = useState(false);

    const [momSysZvwMaterialGrid, setMomSysZvwMaterialGrid] = useState();
    const [, setMomSysZvwMaterialApi] = useState();
    const [momSysZvwMaterialSelect, setMomSysZvwMaterialSelect] = useState();

    const [notification, setNotification] = useState({
        open: false,
        severity: "success",
        msg: "",
    });

    const checkItemValidataion = (schema, formData) => {
        for (const column of schema.columns) {
            let [valid, msg] = checkValidation("create", column, column.validation, formData[column.name] || "");

            if (!valid) {
                notifyWarning(msg);
                return false;
            }
        }

        return true;
    };

    const notifySuccess = (msg) => {
        setNotification({
            severity: "success",
            msg: msg,
            open: true,
        });
    };

    const notifyFailure = (msg) => {
        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const notifyWarning = (msg) => {
        setNotification({
            severity: "warning",
            msg: msg,
            open: true,
        });
    };

    const onNotifySuccess = (msg) => {
        notifySuccess(msg);

        if (onDialogUpdateDetail) {
            onDialogUpdateDetail();
        }
    };

    useEffect(() => {
        (async () => {
            if (momSysZvwMaterialGrid) {
                momSysZvwMaterialGrid.selectionChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setMomSysZvwMaterialSelect(grid.selectedItems[0]);
                        } else {
                            setMomSysZvwMaterialSelect();
                        }
                    },10)
                );

                momSysZvwMaterialGrid.itemsSourceChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setMomSysZvwMaterialSelect(grid.selectedItems[0]);
                        } else {
                            setMomSysZvwMaterialSelect();
                        }
                    },10)
                );
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [momSysZvwMaterialGrid]);

    useEffect(() => {
        if (onSelectMst && momSysZvwMaterialSelect) {
            setFormData((formData) => {
                const newFormData = {
                    ...formData,
                    orderBuyerId: onSelectMst.orderBuyerId,
                    materialCode: momSysZvwMaterialSelect.materialCode,
                    materialCodeVer: momSysZvwMaterialSelect.materialCodeVer,
                    materialName: momSysZvwMaterialSelect.materialName,
                    itemSpecName: momSysZvwMaterialSelect.itemSpecName,
                    itemUnitName: momSysZvwMaterialSelect.itemUnitName,
                    unitCost:
                        momSysZvwMaterialSelect.priceExter === null ? "0" : String(momSysZvwMaterialSelect.priceExter),
                };

                for (const column of schema.columns) {
                    if (column?.defaultValue !== undefined) {
                        newFormData[column.name] = column.defaultValue;
                    }
                }

                return newFormData;
            });
        }
    }, [schema, onSelectMst, momSysZvwMaterialSelect]);


    const onDialogInitialized = (instance) => {
        setDialogInstance(instance);

        if (onInitialized) {
            onInitialized(instance);
        }
    };

    const onApiSuccess = (result) => {
        setKeepOpen((keepOpen) => {
            if (!keepOpen) {
                dialogInstance.hide();
            }

            if (onSuccess) {
                setTimeout(() => onSuccess(result.data), 0);
            } else {
                onNotifySuccess(result.data);
            }

            return keepOpen;
        });
    };

    const onApiFailure = (reason) => {
        if (customizeErrorMsgCallback) {
            notifyFailure(customizeErrorMsgCallback(reason));
        } else {
            notifyFailure(
                t(failureKey("insert", reason.response?.status), { reason: reason.response ? reason.response.data : JSON.stringify(reason) })
            );
        }
    };

    const onOk = (schema, formData) => {
        if (!checkItemValidataion(schema, formData)) {
            console.error("Form Validation Failed");

            return;
        }

        if (schema.validation) {
            let [valid, msg] = checkConditionWithMsg(formData, schema.validation);

            if (!valid) {
                notifyWarning(t("warning.formDataValidationFail", { msg: msg }));
                return;
            }
        }

        /*---------------------------------------------------------------*/
        if (String(formData.qty).includes(".")) {
            notifyWarning(t("warning.formDataValidationFail", { msg: t("error.invalidQtyNumber") }));
            return;
        }
        /*---------------------------------------------------------------*/

        /*----------- 음수 값 검증 -----------*/
        {
            for (const column of schema.columns) {
                if (column.hideInForm || column.readonly) continue;

                if (column.formItemType === "number" || column.formItemType === "float") {
                    const value = Number(formData[column.name]);

                    if (!isNaN(value) && value < 0) {
                        notifyWarning(t("warning.formDataValidationFail", { msg: t("error.negativeNotAllowed") }));
                        return;
                    }
                }
            }
        }
        /*-----------------------------------*/

        // build base api url

        const url = `/api/${schema.name}`;

        const apiData = { ...formData };

        const form = new FormData();

        const json = {};
        const deleted = {};

        for (const formItem in apiData) {
            if (apiData[formItem] !== undefined) {
                json[formItem] = apiData[formItem];
            }
        }

        form.append("deleted", JSON.stringify(deleted));
        form.append("json", JSON.stringify(json));

        apiClient
            .post(url, form, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
            .then((result) => onApiSuccess(result))
            .catch((reason) => onApiFailure(reason));
    };

    const onCancel = () => {
        setMomSysZvwMaterialSelect();

        setOkButtonDisabled(true);

        dialogInstance.hide();
    };

    const checkKeepOpen = () => {
        return (
            <>
                <FormControlLabel
                    control={
                        <Checkbox
                            className={classes.keepOpenSwitch}
                            checked={keepOpen}
                            color="primary"
                            onChange={() => setKeepOpen((keepOpen) => !keepOpen)}
                            name="checkKeepOpen"
                            size="small"
                        />
                    }
                    label={<span className={classes.label}>{t("term.keepDialogOpen")}</span>}
                />
                <div style={{ flexGrow: 1 }}></div>
            </>
        );
    };

    const onChangeFormData = (formData) => {
        setOkButtonDisabled(false);
        setFormData(formData);
    };

    const onCloseNotification = () => {
        setNotification((notification) => {
            return { ...notification, open: false };
        });
    };

    const onCloseDialog = () => {
        if (onClose) {
            onClose();
        }

        setFormData();
    };

    return (
        <Dialog
            id={id}
            moveable={moveable ? moveable : uiConf.dialogMoveable}
            resizable={resizable ? resizable : uiConf.dialogResizable}
            fullWidth={fullWidth ? fullWidth : uiConf.dialogFullWidth}
            maxWidth="lg"
            onClose={onCloseDialog}
            onInitialized={onDialogInitialized}
            {...others}
        >
            <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
            </DialogHeader>

            <Divider />

            <DialogContent style={{ padding: "0px", height: "600px" }}>
                <ReflexContainer orientation="vertical">
                    <ReflexElement flex={0.5}>
                        <BasicCrudScreenType
                            id="MomSysZvwMaterial"
                            headerTitle={t("tableName.MomSysUglOrderBuyerDetailDialog")}
                            view="MomSysZvwMaterial"
                            noCreate
                            noEdit
                            noDelete
                            noExcel
                            noFilter
                            onInitialized={(grid) => {
                                grid.selectionMode = "Row";
                                setMomSysZvwMaterialGrid(grid);
                            }}
                            onApiInitialized={(api) => {
                                setMomSysZvwMaterialApi(api);
                            }}
                            onModifyViewUrl={(url) => url + "/orderBuyerMaterial"}
                            embededInOtherScreenType
                        />
                    </ReflexElement>

                    <ReflexSplitter style={{ width: "1px", color: "lightgray" }} />

                    <ReflexElement flex={0.5}>
                        <div
                            style={{
                                padding: "16px",
                            }}
                        >
                            <AutoForm
                                id="order-buyer-detail-create-form"
                                schema={schema}
                                mode="edit"
                                initialData={formData}
                                onChange={onChangeFormData}
                                setInitialDataCallback={(formData) => {
                                    if (momSysZvwMaterialSelect) {
                                        const numQty = Number(formData.qty);
                                        formData.mediumUnitQty = momSysZvwMaterialSelect.mediumUnitQty * numQty;
                                        formData.smallUnitQty = momSysZvwMaterialSelect.smallUnitQty * numQty;
                                    }
                                }}
                                modifyFormDataCallback={(formData, name) => {
                                    if (momSysZvwMaterialSelect) {
                                        let numQty = Number(formData.qty);
                                        let materialMediumQty = momSysZvwMaterialSelect.mediumUnitQty ? momSysZvwMaterialSelect.mediumUnitQty : 1;
                                        let materialSmallQty = momSysZvwMaterialSelect.smallUnitQty ? momSysZvwMaterialSelect.smallUnitQty : 1;

                                        if (name === "mediumUnitQty") {
                                            numQty =
                                                Number(formData.mediumUnitQty) / materialMediumQty;
                                        } else if (name === "smallUnitQty") {
                                            numQty =
                                                Number(formData.smallUnitQty) / materialSmallQty;
                                        }

                                        formData.qty = numQty;
                                        formData.mediumUnitQty = materialMediumQty * numQty;
                                        formData.smallUnitQty = materialSmallQty * numQty;
                                    }
                                }}
                            />
                        </div>
                    </ReflexElement>
                </ReflexContainer>

                <Snackbar
                    open={notification.open && notification.severity !== "success"}
                    autoHideDuration={uiConf.dialogNotifyAutoHideDuration}
                    onClose={onCloseNotification}
                >
                    <Alert onClose={onCloseNotification} variant="filled" severity={notification.severity}>
                        {notification.msg}
                    </Alert>
                </Snackbar>
            </DialogContent>

            <Divider />

            <DialogActionsOkCancel
                firstActionComponent={checkKeepOpen}
                labels={[t("common.add")]}
                buttonDisabled={[okButtonDisabled, false]} /* [okButtonDisabled, cancelButtonDisabled] */
                onOk={() => onOk(schema, formData)}
                onCancel={onCancel}
            />
        </Dialog>
    );
}

export default MomSysOrderBuyerDetailDialog;
